import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, MapPin, Phone, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCenters = () => {
  const { token } = useAuth();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', map_url: '', is_main: false });

  useEffect(() => { fetchCenters(); }, []);

  const fetchCenters = async () => {
    try { const res = await axios.get(`${API_URL}/api/centers`); setCenters(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (editing) { await axios.put(`${API_URL}/api/centers/${editing.id}`, formData, { headers }); toast.success('Updated!'); }
      else { await axios.post(`${API_URL}/api/centers`, formData, { headers }); toast.success('Added!'); }
      setDialogOpen(false); resetForm(); fetchCenters();
    } catch (e) { toast.error('Failed'); }
  };

  const openEdit = (c) => { setEditing(c); setFormData(c); setDialogOpen(true); };
  const resetForm = () => { setEditing(null); setFormData({ name: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', map_url: '', is_main: false }); };

  return (
    <AdminLayout title="Centers">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground">Manage institute centers.</p>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button data-testid="add-center-btn"><Plus className="w-4 h-4 mr-2" /> Add Center</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Add'} Center</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Name</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required /></div>
                <div className="space-y-2"><Label>City</Label><Input value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} required /></div>
              </div>
              <div className="space-y-2"><Label>Address</Label><Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} required /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>State</Label><Input value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} required /></div>
                <div className="space-y-2"><Label>Pincode</Label><Input value={formData.pincode} onChange={(e) => setFormData({...formData, pincode: e.target.value})} required /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required /></div>
              </div>
              <div className="space-y-2"><Label>Map URL</Label><Input value={formData.map_url} onChange={(e) => setFormData({...formData, map_url: e.target.value})} placeholder="https://maps.google.com/..." /></div>
              <div className="flex items-center gap-2"><Switch checked={formData.is_main} onCheckedChange={(c) => setFormData({...formData, is_main: c})} /><Label>Main Campus</Label></div>
              <Button type="submit" className="w-full">{editing ? 'Update' : 'Add'} Center</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (<div className="grid md:grid-cols-2 gap-4">{[1,2].map(i => <Card key={i} className="animate-pulse"><CardContent className="h-32"></CardContent></Card>)}</div>) : centers.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {centers.map((center) => (
            <Card key={center.id} data-testid={`center-${center.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><MapPin className="w-6 h-6 text-primary" /></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1"><h4 className="font-semibold">{center.name}</h4>{center.is_main && <Badge className="bg-secondary">Main</Badge>}</div>
                    <p className="text-sm text-muted-foreground">{center.address}, {center.city}, {center.state}</p>
                    <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{center.phone}</span>
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{center.email}</span>
                    </div>
                  </div>
                  <Button variant="outline" size="icon" onClick={() => openEdit(center)}><Edit className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (<Card><CardContent className="py-16 text-center"><MapPin className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" /><h3 className="font-semibold">No Centers</h3></CardContent></Card>)}
    </AdminLayout>
  );
};

export default AdminCenters;
